import {
  getToken,
  onMessage,
  isSupported as isMessagingSupportedRaw,
} from 'firebase/messaging';
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { db, getMessagingInstance } from '../config/firebase';
import { COLLECTIONS } from '../config/constants';
import { AppError, FirestoreError } from '../lib/errors';

/**
 * Wrappers del SDK de FCM para el cliente.
 *
 * Flujo end-to-end del registro:
 *   1. El usuario inicia sesión y entra al hogar.
 *   2. La app le pide permiso de notificaciones (en /settings o tras una
 *      acción específica, NO en el splash — molesto y reduce conversion).
 *   3. Si el user acepta: getToken(messaging, {vapidKey, serviceWorkerRegistration})
 *      devuelve un token único de este browser/device.
 *   4. Guardamos el token en `users/{uid}.fcmTokens` con arrayUnion.
 *   5. El worker externo (Fase 7.C) lee esos tokens cuando manda push.
 *
 * Limitaciones conocidas:
 *   - Safari (iOS/macOS): soporta Web Push desde iOS 16.4, pero requiere
 *     que la app esté instalada como PWA (Add to Home Screen). En un browser
 *     plain, isSupported() retorna false.
 *   - Firefox y Chrome: funcionan en cualquier tab desde el primer momento.
 *   - El user puede revocar el permiso en cualquier momento desde la UI del
 *     browser. No hay forma programática de detectarlo en tiempo real —
 *     el próximo getToken devuelve null o tira error.
 */

const SW_PATH = '/firebase-messaging-sw.js';

/**
 * Detecta si el browser soporta FCM. Cachea el resultado.
 * Razón del cache: isSupported() hace una check costosa la primera vez.
 */
let supportedCache = null;
export const isFcmSupported = async () => {
  if (supportedCache !== null) return supportedCache;
  try {
    supportedCache = await isMessagingSupportedRaw();
  } catch {
    supportedCache = false;
  }
  return supportedCache;
};

/**
 * Detecta si estamos en iOS Safari SIN haber instalado la PWA. iOS soporta
 * Web Push desde 16.4 pero SOLO si la app está agregada a la pantalla de
 * inicio (modo "standalone"). En un Safari plain, getToken() falla con un
 * error genérico que confunde al usuario; mejor detectar este caso temprano
 * y dar instrucciones concretas.
 *
 * Retorna true si: agent es iOS + display-mode != standalone + navigator.standalone != true.
 */
export const isIOSWithoutPWA = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return false;
  const isStandalone =
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;
  return !isStandalone;
};

/**
 * Devuelve el estado actual del permiso de notificaciones.
 * Posibles valores: 'granted' | 'denied' | 'default' | 'unsupported'.
 * 'unsupported' es nuestro extra para cuando el browser no implementa Notification.
 */
export const getNotificationPermission = () => {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
};

/**
 * Registra el service worker de FCM. Lo hacemos solo cuando el user
 * efectivamente activa las notificaciones, no en el bootstrap de la app
 * (evita pedir permisos antes de tiempo y consumir bandwith para users
 * que no van a usar push).
 *
 * La firebaseConfig se pasa por query string porque el SW no lee
 * import.meta.env (es archivo estático). Ver public/firebase-messaging-sw.js.
 */
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    throw new AppError(
      'Tu navegador no soporta notificaciones push.',
      'SW_UNSUPPORTED'
    );
  }

  const params = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
  });

  // Si ya está registrado, reutilizamos. navigator.serviceWorker.register
  // es idempotente — devuelve el mismo registro si el SW no cambió.
  return navigator.serviceWorker.register(`${SW_PATH}?${params.toString()}`, {
    scope: '/',
  });
};

/**
 * Pide permiso de notificaciones al browser. Devuelve el nuevo estado.
 * Llamar SOLO en respuesta a una acción del usuario (click), nunca al
 * cargar la app — los browsers modernos rate-limitan e incluso bloquean
 * permanentemente las páginas que abusan.
 */
export const requestNotificationPermission = async () => {
  if (typeof Notification === 'undefined') return 'unsupported';

  // Si ya está decidido (granted o denied), no volvemos a pedir.
  if (Notification.permission !== 'default') {
    return Notification.permission;
  }

  return Notification.requestPermission();
};

/**
 * Obtiene un token FCM para este device/browser y lo guarda en
 * `users/{uid}.fcmTokens` (arrayUnion para no pisar tokens de otros devices).
 *
 * Códigos de error que tira (para que la UI pueda mostrar mensajes específicos):
 *   - 'INVALID_INPUT'         — uid no provisto.
 *   - 'IOS_REQUIRES_PWA'      — iOS Safari sin la app instalada como PWA.
 *   - 'FCM_NOT_SUPPORTED'     — Firebase isSupported() devolvió false.
 *   - 'VAPID_MISSING'         — env var VITE_FIREBASE_VAPID_KEY ausente (caso dev).
 *   - 'MESSAGING_INIT_FAILED' — getMessagingInstance() retornó null.
 *   - 'SW_UNSUPPORTED'        — el browser no expone navigator.serviceWorker.
 *   - 'SW_REGISTRATION_FAILED'— navigator.serviceWorker.register() tiró (cause incluido).
 *   - 'PERMISSION_BLOCKED'    — el browser revocó el permiso entre granted y getToken.
 *   - 'INVALID_VAPID_KEY'     — VAPID inválida (config rota).
 *   - 'TOKEN_NULL'            — getToken resolvió null sin tirar (causa común: IndexedDB
 *                               no disponible, modo privado, SW no terminó de instalarse).
 *   - 'FCM_GENERIC_ERROR'     — cualquier otro error de getToken.
 *
 * Cada return temprano antes era un `null` silencioso; ahora son throws con
 * código explícito para que la UI pueda guiar al usuario.
 */
export const registerFcmToken = async (uid) => {
  if (!uid) throw new AppError('uid requerido', 'INVALID_INPUT');

  // 1. iOS Safari no-PWA: chequeo PRE-todo para dar instrucciones útiles
  //    en vez del genérico "no soporta".
  if (isIOSWithoutPWA()) {
    console.warn('[fcm] iOS Safari fuera de PWA — push requiere "Agregar a inicio".');
    throw new AppError(
      'Para activar notificaciones en iPhone, primero agregá PetCare a tu pantalla de inicio desde el botón de compartir.',
      'IOS_REQUIRES_PWA'
    );
  }

  // 2. Firebase isSupported(). Cubre browsers viejos, in-app webviews, etc.
  const supported = await isFcmSupported();
  if (!supported) {
    console.warn('[fcm] isSupported() = false.');
    throw new AppError(
      'Tu navegador no soporta notificaciones push. Probá con Chrome, Firefox o Safari actualizado.',
      'FCM_NOT_SUPPORTED'
    );
  }

  // 3. VAPID key (caso dev / mal configurado).
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.warn('[fcm] VITE_FIREBASE_VAPID_KEY no configurada.');
    throw new AppError(
      'Configuración de notificaciones incompleta. Avisale al equipo.',
      'VAPID_MISSING'
    );
  }

  // 4. Instancia de Messaging.
  const messaging = await getMessagingInstance();
  if (!messaging) {
    console.warn('[fcm] getMessagingInstance() = null.');
    throw new AppError(
      'Tu navegador no soporta el servicio de mensajería. Probá con otro browser.',
      'MESSAGING_INIT_FAILED'
    );
  }

  // 5. Service Worker registration (uno de los puntos de falla más comunes
  //    en mobile cuando el scope no está autorizado por header).
  let swRegistration;
  try {
    swRegistration = await registerServiceWorker();
    console.info('[fcm] SW registrado, scope:', swRegistration?.scope);
  } catch (err) {
    console.error('[fcm] SW registration falló:', err);
    // SW_UNSUPPORTED ya viene tipado desde registerServiceWorker.
    if (err instanceof AppError) throw err;
    throw new AppError(
      'Tu navegador bloqueó el registro del servicio de notificaciones. Probá desactivar el modo privado o agregar la app a la pantalla de inicio.',
      'SW_REGISTRATION_FAILED',
      err
    );
  }

  // 6. getToken (puede fallar por VAPID, permiso revocado, o blandamente
  //    devolver null si el SW todavía no está activo).
  let token;
  try {
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });
  } catch (err) {
    console.error('[fcm] getToken falló:', err);
    if (err?.code === 'messaging/permission-blocked') {
      throw new AppError(
        'Bloqueaste las notificaciones. Activalas desde la configuración del navegador.',
        'PERMISSION_BLOCKED'
      );
    }
    if (err?.code?.includes('vapid') || err?.code === 'messaging/token-subscribe-failed') {
      throw new AppError(
        'Configuración de notificaciones inválida. Avisale al equipo.',
        'INVALID_VAPID_KEY',
        err
      );
    }
    throw new AppError(
      'Firebase no pudo generar un token para este dispositivo. Esperá unos segundos y reintentá.',
      'FCM_GENERIC_ERROR',
      err
    );
  }

  if (!token) {
    // Causa más común: el SW está instalado pero todavía no es "controller"
    // del client (toma 1-2 segs en el primer registro). También puede ser
    // IndexedDB no disponible (in-app browsers, modo privado).
    console.warn('[fcm] getToken devolvió null sin error.');
    throw new AppError(
      'No pudimos generar un token todavía. Esperá unos segundos y tocá "Registrar este dispositivo" otra vez.',
      'TOKEN_NULL'
    );
  }

  // 7. Guardar en users.fcmTokens. arrayUnion es idempotente — si ya estaba,
  //    no agrega duplicado.
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
      fcmTokens: arrayUnion(token),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    throw new FirestoreError('No se pudo guardar el token de notificaciones', err);
  }

  console.info('[fcm] Token registrado correctamente.');
  return token;
};

/**
 * Quita un token específico de `users/{uid}.fcmTokens`. Útil cuando:
 *   - El user cierra sesión (cleanup).
 *   - El user revoca permisos de notificaciones desde la UI in-app.
 *
 * El worker (Fase 7.C) también limpia tokens muertos cuando FCM le
 * devuelve `registration-token-not-registered`, así que este flow es
 * complementario, no único.
 */
export const unregisterFcmToken = async (uid, token) => {
  if (!uid || !token) return;
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
      fcmTokens: arrayRemove(token),
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn('[notificationService] No se pudo desregistrar el token:', err);
  }
};

/**
 * Wrapper de onMessage para mensajes en FOREGROUND (app abierta y tab activa).
 * En este caso el SW NO muestra notification — la UI tiene que reaccionar
 * (típicamente: toast in-app).
 *
 * Retorna unsubscribe. El caller (componente / hook) lo llama en cleanup.
 */
export const subscribeToForegroundMessages = async (handler) => {
  const supported = await isFcmSupported();
  if (!supported) return () => {};

  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};

  return onMessage(messaging, handler);
};
